import React, { useState } from 'react';
import { MysteryReward } from '../types';
import { useLanguage } from '../context/LanguageContext';
import {
  Gift,
  Sparkles,
  Zap,
  Shield,
  FileText,
  Award,
  CheckCircle,
  X,
  Lock,
  Unlock,
  ChevronRight
} from 'lucide-react';

interface MysteryRewardModalProps {
  reward: MysteryReward;
  onClose: () => void;
}

export const MysteryRewardModal: React.FC<MysteryRewardModalProps> = ({ reward, onClose }) => {
  const { lang, t } = useLanguage();
  const [isOpened, setIsOpened] = useState(false);

  const getRarityBadge = (rarity: MysteryReward['rarity']) => {
    switch (rarity) {
      case 'LEGENDARY':
        return {
          bg: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
          border: '#fbbf24',
          label: t('★ RECOMPENSA LEGENDARIA ★', '★ LEGENDARY REWARD ★'),
          glow: '0 0 30px rgba(245, 158, 11, 0.6)',
        };
      case 'EPIC':
        return {
          bg: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
          border: '#c084fc',
          label: t('◆ RECOMPENSA ÉPICA ◆', '◆ EPIC REWARD ◆'),
          glow: '0 0 25px rgba(139, 92, 246, 0.5)',
        };
      case 'RARE':
        return {
          bg: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
          border: '#60a5fa',
          label: t('▲ RECOMPENSA RARA ▲', '▲ RARE REWARD ▲'),
          glow: '0 0 20px rgba(59, 130, 246, 0.4)',
        };
      default:
        return {
          bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          border: '#34d399',
          label: t('● RECOMPENSA ESPECIAL ●', '● SPECIAL REWARD ●'),
          glow: '0 0 15px rgba(16, 185, 129, 0.3)',
        };
    }
  };

  const rarityMeta = getRarityBadge(reward.rarity);

  const renderIcon = () => {
    switch (reward.icon) {
      case 'Sparkles':
        return <Sparkles size={48} color="#fbbf24" />;
      case 'Zap':
        return <Zap size={48} color="#f59e0b" />;
      case 'Shield':
        return <Shield size={48} color="#8b5cf6" />;
      case 'FileText':
        return <FileText size={48} color="#38bdf8" />;
      case 'Award':
        return <Award size={48} color="#34d399" />;
      default:
        return <Gift size={48} color="#f43f5e" />;
    }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div
        className="modal-content glass-card"
        style={{
          maxWidth: '540px',
          textAlign: 'center',
          padding: '36px 28px',
          position: 'relative',
          overflow: 'hidden',
          border: `1px solid ${isOpened ? rarityMeta.border : 'rgba(245, 158, 11, 0.5)'}`,
          boxShadow: isOpened ? rarityMeta.glow : '0 0 40px rgba(245, 158, 11, 0.25)',
          background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(10, 15, 29, 0.98) 100%)',
        }}
      >
        {/* Subtle background rays */}
        {isOpened && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '400px',
              height: '400px',
              background: 'radial-gradient(circle, rgba(245, 158, 11, 0.15) 0%, transparent 70%)',
              pointerEvents: 'none',
              animation: 'pulse 2s infinite',
            }}
          />
        )}

        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'transparent',
            border: 'none',
            color: 'var(--text-muted)',
            cursor: 'pointer',
          }}
        >
          <X size={20} />
        </button>

        {!isOpened ? (
          <div>
            <div style={{ marginBottom: '14px', display: 'inline-flex', padding: '16px', background: 'rgba(245, 158, 11, 0.12)', borderRadius: '50%', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
              <Gift size={56} color="#f59e0b" style={{ animation: 'bounce 1.5s infinite' }} />
            </div>

            <div style={{ fontSize: '11px', fontWeight: 800, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>
              {t('Programación de Recompensa Variable (Dopamina)', 'Variable Reward Schedule (Dopamine Loop)')}
            </div>

            <h2 style={{ fontSize: '24px', fontWeight: 800, margin: '0 0 10px', color: 'var(--text-primary)' }}>
              {t('¡Caja Misteriosa Desbloqueada!', 'Mystery Loot Box Unlocked!')}
            </h2>

            <p style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '24px', maxWidth: '420px', margin: '0 auto 24px' }}>
              {t(
                'Por tu destacada toma de decisiones en el simulador de crisis corporativo, has obtenido una recompensa aleatoria de cumplimiento.',
                'For your decisive leadership in the corporate crisis simulator, you have unlocked an unpredictable compliance loot box.'
              )}
            </p>

            <button
              className="btn btn-primary"
              style={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                boxShadow: '0 0 20px rgba(245, 158, 11, 0.5)',
                padding: '14px 28px',
                fontSize: '15px',
                fontWeight: 800,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
              }}
              onClick={() => setIsOpened(true)}
            >
              <Unlock size={18} />
              {t('Abrir Caja de Recompensa Sorpresa', 'Open Surprise Mystery Box')}
            </button>
          </div>
        ) : (
          <div>
            <div
              style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: '20px',
                background: rarityMeta.bg,
                color: '#ffffff',
                fontSize: '11px',
                fontWeight: 900,
                letterSpacing: '0.8px',
                marginBottom: '16px',
              }}
            >
              {rarityMeta.label}
            </div>

            <div style={{ marginBottom: '16px', display: 'inline-flex', padding: '20px', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '24px', border: `1px solid ${rarityMeta.border}` }}>
              {renderIcon()}
            </div>

            <h3 style={{ fontSize: '20px', fontWeight: 800, margin: '0 0 8px', color: 'var(--text-primary)' }}>
              {reward.title[lang]}
            </h3>

            <p style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '20px', maxWidth: '420px', margin: '0 auto 20px' }}>
              {reward.description[lang]}
            </p>

            <div
              style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
                padding: '12px 16px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{t('Elemento añadido a tu perfil:', 'Added to your profile:')}</span>
              <code style={{ fontSize: '13px', fontWeight: 700, color: '#38bdf8' }}>{reward.value}</code>
            </div>

            <button
              className="btn btn-primary"
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
              onClick={onClose}
            >
              <CheckCircle size={16} />
              {t('Equipar y Continuar', 'Claim & Continue')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
